/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  useForm,
  getSubmitButtonProps,
  RichTextEditor,
} from '@marageti/z4-lib';
import {
  Button,
  Alert,
  AlertTitle,
  Stack,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { LoadingButton } from '@mui/lab';
import { Controller } from 'react-hook-form';

const schema = z.object({
  message: z.string().min(1, 'Required'),
});

type NoteViewProps = {
  closeDialog: () => void;
  error: Error | null;
  isInternal: boolean;
  onSubmit: (data: any) => void;
  open: boolean;
};

const NoteView = ({
  closeDialog,
  error,
  isInternal,
  onSubmit,
  open,
}: NoteViewProps) => {
  const {
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    control,
  } = useForm({
    resolver: zodResolver(schema),
  });

  return (
    <Dialog
      open={open}
      onClose={closeDialog}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ px: 3 }}>
        {isInternal ? 'Log Internal Note' : 'Log Note'}
      </DialogTitle>
      <DialogContent sx={{
        p: 0,
        m: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
      >
        {error && (
        <Alert severity="error">
          <AlertTitle>There was an error submitting note</AlertTitle>
          {error?.message}
        </Alert>
        )}
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack
            spacing={1.5}
            sx={{
              px: 3,
              pt: 1,
              pb: 2.5,
              flex: 1,
              overflowY: 'auto',
            }}
          >

            <Controller
              control={control}
              name="message"
              render={({ field }) => (
                <RichTextEditor
                  editorName="Add-Note-Editor"
                  error={!!errors.message}
                  label="Message"
                  onChange={field.onChange}
                  sx={{
                    maxHeight: '225px',
                    minHeight: '175px',
                    flex: 1,
                  }}
                  useLinks
                  useLists
                />
              )}
            />
            {isInternal && (
              <Typography variant="caption" color="text.secondary">
                Internal notes will not be seen by the traveler.
              </Typography>
            )}
          </Stack>
          <Stack
            direction="row"
            spacing={2}
            sx={(theme) => ({
              borderTop: `1px solid ${theme.palette.divider}`,
              py: 2.5,
              px: 3,
              flexShrink: 0,
            })}
          >
            <LoadingButton
              type="submit"
              variant="contained"
              sx={{
                width: { desktop: 'fit-content' },
              }}
              {...getSubmitButtonProps(isSubmitting, isValid)}
            >
              Submit
            </LoadingButton>
            <Button
              variant="outlined"
              onClick={closeDialog}
              sx={{
                width: { desktop: 'fit-content' },
              }}
            >
              Cancel
            </Button>
          </Stack>

        </form>
      </DialogContent>
    </Dialog>
  );
};

export default NoteView;
